import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import type { AgGridReact as AgGridReactType } from "ag-grid-react";
import type { OptionFilterModel } from "../filters/OptionFilter";
import OptionFilter, { type Option } from "../filters/OptionFilter";
import PaginationBar from "../PaginationBar";
import SnackbarUtils from "utils/SnackbarUtils";
import ActionCellRenderer from "./ActionCellRenderer";
import {
  AG_GRID_LOCALE_DEFAULT,
  AG_GRID_PAGE_SIZE_OPTIONS,
} from "./agGridTableState";
import type {
  AgGridTableProps,
  ColumnConfig,
  ColumnFilterConfig,
  GridDataMode,
  QueryState,
} from "./agGridTable.types";
import {
  CellEditingStartedEvent,
  CellValueChangedEvent,
  ColDef,
  FilterChangedEvent,
  GridReadyEvent,
  SortChangedEvent,
} from "ag-grid-community";

const QUERY_REASON = {
  PAGINATION: "pagination",
  FILTER: "filter",
  SORT: "sort",
} as const;

const AgGridTable = <T,>({
  rows = [] as T[],
  columns,
  height = 520,
  autoHeight = true,
  pinnedLeftKeys = [],
  actionHeaderName = "Hành động",
  onEdit,
  onDelete,
  filtersConfig,
  onSave,
  onQueryChange,
  onGridReady,
  onSortChanged,
  onFilterChanged,
  filterMode = "local",
  sortMode = "local",
  onCellValueChanged,
  getRowId,
  onAfterActions,
  stopEditingWhenCellsLoseFocus,
  suppressClickEdit,
  onRowClicked,
  onSelectionChanged,
  defaultColDef: defaultColDefProp,
  pagination = true,
  pageSize = 20,
  totalPages: totalPagesProp,
  showAddRow,
  addRowData,
  rowHeight,
  suppressCellFocus,
  immutableData,
  deltaRowDataMode,
}: AgGridTableProps<T>) => {
  const gridRef = useRef<AgGridReactType<T>>(null);
  const showActions = Boolean(onEdit || onDelete || onSave);
  const enableInlineEdit = Boolean(onSave);
  const [paginationInfo, setPaginationInfo] = useState(() => ({
    pageIndex: 0,
    page: 1,
    pageSize,
    totalPages: totalPagesProp as number | undefined,
    isLastPageFound: totalPagesProp !== undefined ? true : undefined,
  }));

  const [dirtyRowIds, setDirtyRowIds] = useState<Set<string>>(() => new Set());
  const dirtyRowIdsRef = useRef<Set<string>>(new Set());
  const trackingEnabled = Boolean(onSave);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const editingRowIdRef = useRef<number | null>(null);

  // Refs for stable callbacks in columnDefs
  const onEditRef = useRef(onEdit);
  const onDeleteRef = useRef(onDelete);
  const onSaveRef = useRef(onSave);
  const onAfterActionsRef = useRef(onAfterActions);

  // Keep refs in sync with props/state
  useEffect(() => {
    editingRowIdRef.current = editingRowId;
    onEditRef.current = onEdit;
    onDeleteRef.current = onDelete;
    onSaveRef.current = onSave;
    onAfterActionsRef.current = onAfterActions;
  }, [editingRowId, onEdit, onDelete, onSave, onAfterActions]);

  const effectiveFilterMode: GridDataMode = filterMode ?? "server";
  const effectiveSortMode: GridDataMode = sortMode ?? "local";
  const pageSizeOptions = useMemo<number[]>(() => {
    if (!AG_GRID_PAGE_SIZE_OPTIONS.includes(pageSize)) {
      return [pageSize, ...AG_GRID_PAGE_SIZE_OPTIONS].sort((a, b) => a - b);
    }
    return AG_GRID_PAGE_SIZE_OPTIONS;
  }, [pageSize]);
  const gridOptions = useMemo(
    () => ({
      localeText: AG_GRID_LOCALE_DEFAULT,
      ...(immutableData ? { immutableData: true } : {}),
      ...(deltaRowDataMode ? { deltaRowDataMode: true } : {}),
    }),
    [deltaRowDataMode, immutableData],
  );
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: (params) => {
        // Use ref to avoid recreating defaultColDef on editingRowId change
        return (
          params.data?.id === editingRowIdRef.current ||
          params.node.rowPinned === "top"
        );
      },
      minWidth: 140,
      ...(defaultColDefProp || {}),
    }),
    [defaultColDefProp], // Remove editingRowId from deps - use ref instead
  );

  useEffect(() => {
    showAddRow !== "edit" && setEditingRowId(null);
  }, [showAddRow]);

  useEffect(() => {
    if (totalPagesProp === undefined) return;
    setPaginationInfo((prev) => {
      const clampedPageIndex = Math.min(
        prev.pageIndex,
        Math.max(totalPagesProp - 1, 0),
      );
      const next = {
        ...prev,
        pageIndex: clampedPageIndex,
        page: clampedPageIndex + 1,
        totalPages: totalPagesProp,
        isLastPageFound: true,
      };
      const unchanged =
        prev.pageIndex === next.pageIndex &&
        prev.page === next.page &&
        prev.totalPages === next.totalPages &&
        prev.isLastPageFound === next.isLastPageFound;
      return unchanged ? prev : next;
    });
  }, [totalPagesProp]);

  const buildQueryState = useCallback(
    (paginationOverride?: Partial<QueryState["pagination"]>): QueryState => {
      const api = gridRef.current?.api;
      const nextPagination = {
        pageIndex:
          paginationOverride?.pageIndex ?? paginationInfo.pageIndex ?? 0,
        page:
          paginationOverride?.page ??
          (paginationOverride?.pageIndex ?? paginationInfo.pageIndex ?? 0) + 1,
        pageSize:
          paginationOverride?.pageSize ?? paginationInfo.pageSize ?? pageSize,
        totalPages:
          paginationOverride?.totalPages ??
          totalPagesProp ??
          paginationInfo.totalPages,
        isLastPageFound:
          paginationOverride?.isLastPageFound ??
          (totalPagesProp !== undefined
            ? true
            : paginationInfo.isLastPageFound),
      };

      return {
        filterModel: api?.getFilterModel() ?? {},
        sortModel: api?.getState().sort?.sortModel ?? [],
        pagination: nextPagination,
      };
    },
    [
      pageSize,
      paginationInfo.isLastPageFound,
      paginationInfo.pageIndex,
      paginationInfo.pageSize,
      paginationInfo.totalPages,
      totalPagesProp,
    ],
  );

  const resolveRowId = useCallback(
    (
      data: T,
      nodeId?: string,
      rowIndex?: number,
      pinned?: string | null,
    ): string | undefined => {
      if (pinned) {
        return `pinned-${pinned}-${rowIndex ?? ""}`;
      }
      if (getRowId) {
        try {
          const value = getRowId({ data, rowIndex } as any);
          if (value) return value;
        } catch {
          // fallback when getRowId throws
        }
      }
      const dataId = (data as any)?.id;
      if (dataId !== undefined && dataId !== null) {
        return String(dataId);
      }
      if (nodeId) return String(nodeId);
      if (rowIndex !== undefined && rowIndex !== null) return String(rowIndex);
      return undefined;
    },
    [getRowId],
  );

  const markRowDirty = useCallback(
    (rowId?: string) => {
      if (!rowId || !trackingEnabled) return;
      setDirtyRowIds((prev) => {
        if (prev.has(rowId)) return prev;
        const next = new Set(prev);
        next.add(rowId);
        return next;
      });
    },
    [trackingEnabled],
  );

  const clearRowDirty = useCallback((rowId?: string) => {
    if (!rowId) return;
    setDirtyRowIds((prev) => {
      if (!prev.has(rowId)) return prev;
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
    setEditingRowId(null);
  }, []);

  // Consolidated effect for dirtyRowIds management
  useEffect(() => {
    // Reset when tracking disabled — use functional update to avoid new ref
    if (!trackingEnabled) {
      setDirtyRowIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    // Sync ref
    dirtyRowIdsRef.current = dirtyRowIds;

    // Refresh action column cells
    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells({
        columns: ["__actions"],
        force: true,
      });
    }

    // Filter out deleted rows
    if (dirtyRowIds.size > 0) {
      const existingIds = new Set<string>();
      rows.forEach((row, index) => {
        const id = resolveRowId(row, undefined, index);
        if (id) existingIds.add(id);
      });

      setDirtyRowIds((prev) => {
        if (!prev.size) return prev;
        const next = new Set(
          Array.from(prev).filter((id) => existingIds.has(id)),
        );
        return next.size === prev.size ? prev : next;
      });
    }
  }, [trackingEnabled, dirtyRowIds, rows, resolveRowId]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const withPinned = columns.map((col) => {
      const key = col.field || col.colId;
      if (!key) return col;

      const next: ColDef = {
        ...col,
        ...(pinnedLeftKeys.includes(key) ? { pinned: "left" as const } : {}),
      };

      const filterConfig = filtersConfig ? filtersConfig[String(key)] : null;
      if (!filterConfig) return next;

      const onValueChange = (value: string | string[] | null) => {
        const api = gridRef.current?.api;
        if (!api) return;

        const state = buildQueryState();
        const filterValues = Array.isArray(value)
          ? value.filter(Boolean)
          : value
            ? [value]
            : [];
        const nextFilterModel: OptionFilterModel | undefined =
          filterValues.length
            ? { filterType: "optionFilter", values: filterValues }
            : undefined;

        const mergedValue = {
          ...state,
          filterModel: { ...state.filterModel },
        };
        if (nextFilterModel) {
          mergedValue.filterModel[key as string] = nextFilterModel;
        } else {
          delete mergedValue.filterModel[key as string];
        }
        filterConfig.onChange?.(mergedValue);
      };

      return {
        ...next,
        filter: OptionFilter as any,
        filterParams: {
          ...(next.filterParams || {}),
          options: filterConfig.options,
          mode: filterConfig.type,
          onValueChange,
        },
      };
    });

    const actionCol: ColDef | null = showActions
      ? {
          headerName: actionHeaderName,
          field: "__actions",
          pinned: "right",
          filter: false,
          floatingFilter: false,
          sortable: false,
          editable: false,
          menuTabs: [],
          width: 130,
          minWidth: 100,
          cellRenderer: (params: any) => (
            <ActionCellRenderer
              params={params}
              editingRowId={editingRowIdRef.current}
              enableInlineEdit={enableInlineEdit}
              onSave={onSaveRef.current}
              onEdit={onEditRef.current}
              onDelete={onDeleteRef.current}
              onAfterActions={onAfterActionsRef.current}
              clearRowDirty={clearRowDirty}
              resolveRowId={resolveRowId}
              setEditingRowId={setEditingRowId}
            />
          ),
        }
      : null;

    return actionCol ? [...withPinned, actionCol] : withPinned;
  }, [
    columns,
    pinnedLeftKeys,
    filtersConfig,
    buildQueryState,
    actionHeaderName,
    showActions,
    resolveRowId,
    clearRowDirty,
    enableInlineEdit,
  ]);

  const emitQueryChange = useCallback(
    (
      paginationOverride?: Partial<QueryState["pagination"]>,
      reason:
        | typeof QUERY_REASON.PAGINATION
        | typeof QUERY_REASON.FILTER
        | typeof QUERY_REASON.SORT = QUERY_REASON.PAGINATION,
    ) => {
      if (!onQueryChange) return;
      const shouldEmit =
        reason === QUERY_REASON.SORT
          ? effectiveSortMode === "server"
          : reason === QUERY_REASON.FILTER
            ? effectiveFilterMode === "server"
            : true;

      if (!shouldEmit) return;
      const state = buildQueryState(paginationOverride);
      onQueryChange(state);
    },
    [buildQueryState, effectiveFilterMode, effectiveSortMode, onQueryChange],
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      onGridReady?.(event);
    },
    [onGridReady],
  );

  const applyPagination = useCallback(
    (
      nextPageIndex: number,
      nextPageSize?: number,
      reason:
        | typeof QUERY_REASON.PAGINATION
        | typeof QUERY_REASON.FILTER
        | typeof QUERY_REASON.SORT = QUERY_REASON.PAGINATION,
    ) => {
      setPaginationInfo((prev) => {
        const total = totalPagesProp ?? prev.totalPages;
        const pageSizeValue = nextPageSize ?? prev.pageSize ?? pageSize;
        const clampedIndex =
          total !== undefined
            ? Math.min(Math.max(0, nextPageIndex), Math.max(total - 1, 0))
            : Math.max(0, nextPageIndex);

        const next: Required<QueryState["pagination"]> = {
          pageIndex: clampedIndex,
          page: clampedIndex + 1,
          pageSize: pageSizeValue,
          totalPages: total,
          isLastPageFound:
            totalPagesProp !== undefined ? true : prev.isLastPageFound,
        };

        const unchanged =
          prev.pageIndex === next.pageIndex &&
          prev.page === next.page &&
          prev.pageSize === next.pageSize &&
          prev.totalPages === next.totalPages &&
          prev.isLastPageFound === next.isLastPageFound;

        if (!unchanged || reason !== QUERY_REASON.PAGINATION) {
          emitQueryChange(next, reason);
        }

        return unchanged ? prev : next;
      });
    },
    [emitQueryChange, pageSize, totalPagesProp],
  );

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      onFilterChanged?.(event);
      if (effectiveFilterMode === "server") {
        applyPagination(0, undefined, QUERY_REASON.FILTER);
      }
    },
    [applyPagination, effectiveFilterMode, onFilterChanged],
  );

  const handleSortChanged = useCallback(
    (event: SortChangedEvent) => {
      onSortChanged?.(event);
      emitQueryChange(undefined, QUERY_REASON.SORT);
    },
    [emitQueryChange, onSortChanged],
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<T>) => {
      const rowId = resolveRowId(
        event.data,
        event.node?.id,
        event.rowIndex,
        event.node?.rowPinned,
      );
      markRowDirty(rowId);
      onCellValueChanged?.(event);
    },
    [markRowDirty, onCellValueChanged, resolveRowId],
  );

  const handleCellEditingStarted = useCallback(
    (event: CellEditingStartedEvent<T>) => {
      if (!trackingEnabled) return;
      if (!dirtyRowIds.size) return;
      const rowId = resolveRowId(
        event.data,
        event.node?.id,
        event.rowIndex,
        event.node?.rowPinned,
      );
      if (rowId && dirtyRowIds.has(rowId)) return;
      SnackbarUtils.warning(
        "Vui lòng lưu dòng đang chỉnh sửa trước khi thêm/chỉnh sửa dòng khác.",
      );
      event.api.stopEditing(true);
    },
    [dirtyRowIds, resolveRowId, trackingEnabled],
  );

  const handleGoToPrevious = useCallback(() => {
    applyPagination(Math.max(0, paginationInfo.pageIndex - 1));
  }, [applyPagination, paginationInfo.pageIndex]);

  const handleGoToNext = useCallback(() => {
    applyPagination(paginationInfo.pageIndex + 1);
  }, [applyPagination, paginationInfo.pageIndex]);

  const handleGoToPage = useCallback(
    (nextPage: number) => {
      const targetIndex = Math.max(0, Math.floor(nextPage) - 1);
      applyPagination(targetIndex);
    },
    [applyPagination],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      applyPagination(0, size);
    },
    [applyPagination],
  );

  const pinnedTopRowData = useMemo<T[] | undefined>(() => {
    if (!showAddRow || showAddRow === "edit") return undefined;
    if (!addRowData) return [{} as T];
    return [{ ...addRowData, editable: true }];
  }, [showAddRow, addRowData]);

  const effectiveTotalPages = totalPagesProp ?? paginationInfo.totalPages;
  const currentPageDisplay = paginationInfo.page ?? 1;

  return (
    <div className="w-full space-y-3">
      <div
        className="ag-theme-alpine w-full"
        style={autoHeight ? undefined : { height }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout={autoHeight ? "autoHeight" : "normal"}
          animateRows
          onGridReady={handleGridReady}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          onCellValueChanged={handleCellValueChanged}
          onCellEditingStarted={handleCellEditingStarted}
          onRowClicked={onRowClicked}
          onSelectionChanged={onSelectionChanged}
          getRowId={getRowId}
          stopEditingWhenCellsLoseFocus={stopEditingWhenCellsLoseFocus}
          suppressClickEdit={suppressClickEdit}
          suppressCellFocus={suppressCellFocus}
          enableFilterHandlers={false}
          pinnedTopRowData={pinnedTopRowData}
          pagination={false}
          rowHeight={rowHeight}
          suppressScrollOnNewData={true}
          gridOptions={gridOptions}
        />
      </div>

      {pagination && (
        <PaginationBar
          page={currentPageDisplay}
          pageSize={paginationInfo.pageSize}
          totalPages={effectiveTotalPages}
          pageSizeOptions={pageSizeOptions}
          onPrev={handleGoToPrevious}
          onNext={handleGoToNext}
          onGoToPage={handleGoToPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
};

export type {
  AgGridTableProps,
  ColumnConfig,
  ColumnFilterConfig,
  Option,
  QueryState,
};
export default AgGridTable;
